import type { Client, FetchResponse, MaybeOptionalInit } from "openapi-fetch";
import type {
  HttpMethod,
  MediaType,
  PathsWithMethod,
  RequiredKeysOf,
} from "openapi-typescript-helpers";

export class ApiError extends Error {
  status: number;
  data: unknown;
  response: Response;

  constructor(response: Response, data: unknown) {
    super(`API request failed: ${response.status} ${response.statusText}`);
    this.status = response.status;
    this.data = data;
    this.response = response;
  }
}

export function toFormData(fields: Record<string, unknown>): FormData {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    formData.append(key, value instanceof Blob ? value : String(value));
  }
  return formData;
}

// mirrors openapi-fetch's own (unexported) InitParam helper: makes the 2nd
// argument optional only when every property on Init is itself optional
type InitParam<Init> = RequiredKeysOf<Init> extends never ? [Init?] : [Init];

type UnwrappedMethod<
  Paths extends Record<string, Record<HttpMethod, {}>>,
  Method extends HttpMethod,
  Media extends MediaType,
> = <
  Path extends PathsWithMethod<Paths, Method>,
  Init extends MaybeOptionalInit<Paths[Path], Method>,
>(
  url: Path,
  ...init: InitParam<Init>
) => Promise<
  NonNullable<FetchResponse<Paths[Path][Method], Init, Media>["data"]>
>;

// NOTE: generated `paths` interfaces have no index signature, so they never
// satisfy this constraint directly - every real usage of this alias below
// needs a `@ts-expect-error` on the instantiation itself. Deriving Paths via
// an `infer`-based conditional type instead (matching openapi-fetch's own
// MethodResponse/ClientPathsWithMethod pattern) avoids that local error, but
// makes the exported type conditional, which silently collapses to `never`
// for every file other than the one importing ApiClient - confirmed via a
// full check-types run swapping between the two approaches. A contained,
// suppressed constraint error here is far preferable to that.
export type UnwrappedClient<
  Paths extends Record<string, Record<HttpMethod, {}>>,
  Media extends MediaType = MediaType,
> = {
  GET: UnwrappedMethod<Paths, "get", Media>;
  PUT: UnwrappedMethod<Paths, "put", Media>;
  POST: UnwrappedMethod<Paths, "post", Media>;
  DELETE: UnwrappedMethod<Paths, "delete", Media>;
  OPTIONS: UnwrappedMethod<Paths, "options", Media>;
  HEAD: UnwrappedMethod<Paths, "head", Media>;
  PATCH: UnwrappedMethod<Paths, "patch", Media>;
  TRACE: UnwrappedMethod<Paths, "trace", Media>;
};

const HTTP_METHODS = [
  "GET",
  "PUT",
  "POST",
  "DELETE",
  "OPTIONS",
  "HEAD",
  "PATCH",
  "TRACE",
] as const;

// Wraps a raw openapi-fetch client so every method returns the parsed success
// data directly (Promise<T>) and throws ApiError on failure, instead of
// resolving to { data, error, response } and requiring a per-call unwrap().
export function unwrapClient<
  Paths extends Record<string, Record<HttpMethod, {}>>,
  Media extends MediaType,
>(client: Client<Paths, Media>): UnwrappedClient<Paths, Media> {
  const wrapped = {} as Record<string, unknown>;
  for (const method of HTTP_METHODS) {
    wrapped[method] = async (url: string, ...init: unknown[]) => {
      const { data, error, response } = await (
        client[method] as (...args: unknown[]) => Promise<{
          data?: unknown;
          error?: unknown;
          response: Response;
        }>
      )(url, ...init);
      // openapi-fetch's own empty-body short-circuit (204, HEAD, or
      // Content-Length: 0) resolves `error: undefined` even when the response
      // isn't ok, breaking its documented data/error contract - see
      // https://github.com/openapi-ts/openapi-typescript/issues/2530.
      // Checking response.ok directly sidesteps that rather than relying on
      // `error` being populated. (SBSB-2912)
      if (!response.ok) {
        throw new ApiError(response, error);
      }
      return data;
    };
  }
  return wrapped as UnwrappedClient<Paths, Media>;
}
